import { useState, type ReactNode } from 'react';
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';

export interface WorkOrderTabbedCardTab {
  label: string;
  content: ReactNode;
}

export interface WorkOrderTabbedCardColumn {
  tabs: WorkOrderTabbedCardTab[];
  ariaLabel: string;
  title?: string;
}

interface WorkOrderTabbedCardProps {
  tabs?: WorkOrderTabbedCardTab[];
  ariaLabel?: string;
  /** Independent tab groups, two columns on desktop and stacked on mobile. */
  columns?: WorkOrderTabbedCardColumn[];
}

function WorkOrderTabGroup({ tabs, ariaLabel }: { tabs: WorkOrderTabbedCardTab[]; ariaLabel: string }) {
  const [activeTab, setActiveTab] = useState(0);
  const safeIndex = activeTab < tabs.length ? activeTab : 0;

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={safeIndex}
          onChange={(_, value: number) => { setActiveTab(value); }}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label={ariaLabel}
        >
          {tabs.map((tab, index) => (
            <Tab key={`${tab.label}-${index}`} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      {tabs[safeIndex]?.content}
    </Box>
  );
}

export function WorkOrderTabbedCard({ tabs, ariaLabel, columns }: WorkOrderTabbedCardProps) {
  const groups: WorkOrderTabbedCardColumn[] = columns
    ?? (tabs != null && ariaLabel != null ? [{ tabs, ariaLabel }] : []);
  const firstGroup = groups[0];

  if (groups.length <= 1) {
    if (firstGroup == null) return null;
    return <WorkOrderTabGroup tabs={firstGroup.tabs} ariaLabel={firstGroup.ariaLabel} />;
  }

  return (
    <Grid container spacing={3} data-testid="work-order-tabbed-columns">
      {groups.map((column, index) => (
        <Grid
          key={column.ariaLabel}
          size={{ xs: 12, md: 6 }}
          data-testid="work-order-tabbed-column"
          sx={{
            minWidth: 0,
            ...(index < groups.length - 1
              ? {
                  borderRight: { md: 1 },
                  borderBottom: { xs: 1, md: 0 },
                  borderColor: 'divider',
                }
              : {}),
          }}
        >
          {column.title != null && column.title !== '' ? (
            <Typography variant="subtitle2" component="p" sx={{ mb: 1 }}>
              {column.title}
            </Typography>
          ) : null}
          <WorkOrderTabGroup tabs={column.tabs} ariaLabel={column.ariaLabel} />
        </Grid>
      ))}
    </Grid>
  );
}
